import { CommonModule } from '@angular/common';
import {
  Component,
  signal,
  inject,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { VOneIconComponent } from '@shared-components';
import { IconType } from '@shared-enums';
import { languageIcon } from '@shared-utils';

import { AuthService } from 'src/app/@shared/authentication/auth.service';

@Component({
  selector: 'app-language-selector',
  standalone: true,
  imports: [CommonModule, MatIconModule, VOneIconComponent, TranslateModule],
  templateUrl: './language-selector.component.html',
  styleUrl: './language-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageSelectorComponent implements OnInit, OnDestroy {
  private readonly translate = inject(TranslateService);
  private router = inject(Router);
  private authService = inject(AuthService);

  private static readonly LANGUAGE_STORAGE_KEY = 'app-language';

  isCollapsed = signal<boolean>(false);
  languageCurrent = signal<string>('');

  unsubscribe$ = new Subject<void>();
  languageIcon = languageIcon;
  readonly ICON_TYPE = IconType;
  readonly languages = [
    { code: 'es', label: 'Languages.Spanish' },
    { code: 'en', label: 'Languages.English' },
  ];

  ngOnInit() {
    const savedLanguage = localStorage.getItem(
      LanguageSelectorComponent.LANGUAGE_STORAGE_KEY,
    );
    const activeLang =
      this.translate.getCurrentLang() ||
      savedLanguage ||
      this.translate.getFallbackLang() ||
      'es';

    this.languageCurrent.set(activeLang);

    this.translate.onLangChange
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((event) => {
        this.languageCurrent.set(event.lang);
      });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  changeLang(lang: string) {
    this.translate.use(lang);
    this.languageCurrent.set(lang);
    localStorage.setItem(LanguageSelectorComponent.LANGUAGE_STORAGE_KEY, lang);
  }

  expandedChild(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }

    this.isCollapsed.update((value) => !value);
  }

  closeMenu() {
    this.isCollapsed.set(false);
  }

  logout() {
    this.authService.logOut();
    this.router.navigate(['/login']);
  }
}
